import { read, add, update, nowIso } from "@/lib/storage";
import { getBook, normTitle } from "@/lib/catalog";
import { dayKey } from "@/lib/hooks";

export function startReading({ title, author, totalPages = 0, source = "manual", catalogId }) {
  let bookId = catalogId;
  if (!bookId) {
    const ex = read("books").find((b) => normTitle(b.title) === normTitle(title));
    bookId = ex ? ex.id : add("books", { title, author, totalPages, source }).id;
  }
  const existing = read("reading_progress").find((p) => p.bookId === bookId && p.status !== "completed");
  if (existing) return { record: existing, created: false };
  const meta = getBook(bookId);
  const record = add("reading_progress", {
    bookId, status: "reading", currentPage: 0,
    totalPages: totalPages || meta?.pages || meta?.totalPages || 0,
    startedAt: nowIso(), completedAt: null, notes: "",
  });
  return { record, created: true };
}

export function logSession({ progressId, minutes, pagesRead, note, date }) {
  const p = read("reading_progress").find((x) => x.id === progressId);
  if (!p) return;
  add("reading_sessions", {
    progressId, bookId: p.bookId, date: date || nowIso(),
    minutes: Number(minutes) || 0, pagesRead: Number(pagesRead) || 0, note: note || "",
  });
  let cur = p.currentPage + (Number(pagesRead) || 0);
  const patch = {};
  if (p.totalPages > 0 && cur >= p.totalPages) {
    cur = p.totalPages;
    patch.status = "completed";
    patch.completedAt = nowIso();
  }
  update("reading_progress", progressId, { ...patch, currentPage: cur });
}

export const pct = (p) => (p.totalPages > 0 ? Math.min(100, Math.round((p.currentPage / p.totalPages) * 100)) : 0);

export function computeStats(progress, sessions, settings) {
  const now = new Date();
  const days = {};
  sessions.forEach((s) => { days[dayKey(s.date)] = (days[dayKey(s.date)] || 0) + s.minutes; });

  const last = (n) => Array.from({ length: n }, (_, i) => {
    const d = new Date(now);
    d.setDate(now.getDate() - (n - 1 - i));
    return { key: dayKey(d), label: d.toLocaleDateString("id-ID", { day: "numeric", month: "short" }), minutes: days[dayKey(d)] || 0 };
  });

  const week = last(7);
  const weekMinutes = week.reduce((a, d) => a + d.minutes, 0);
  const weekSessions = sessions.filter((s) => (now - new Date(s.date)) / 864e5 < 7).length;

  let streak = 0;
  const cursor = new Date(now);
  if (!days[dayKey(cursor)]) cursor.setDate(cursor.getDate() - 1);
  while (days[dayKey(cursor)]) { streak++; cursor.setDate(cursor.getDate() - 1); }

  const totalMinutes = sessions.reduce((a, s) => a + s.minutes, 0);
  const totalPagesRead = sessions.reduce((a, s) => a + s.pagesRead, 0);
  const reading = progress.filter((p) => p.status === "reading");
  const completed = progress.filter((p) => p.status === "completed");
  const year = settings.goalYear || now.getFullYear();
  const completedThisYear = completed.filter((p) => p.completedAt && new Date(p.completedAt).getFullYear() === year).length;
  const monthMinutes = sessions
    .filter((s) => { const d = new Date(s.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); })
    .reduce((a, s) => a + s.minutes, 0);

  const milestones = [
    { id: "m1", label: "Sesi pertama", desc: "Catat sesi membaca pertama", done: sessions.length >= 1 },
    { id: "m2", label: "Konsisten 3 hari", desc: "Streak membaca 3 hari", done: streak >= 3 },
    { id: "m3", label: "Seminggu penuh", desc: "Streak membaca 7 hari", done: streak >= 7 },
    { id: "m4", label: "Satu jam", desc: "Total membaca 60 menit", done: totalMinutes >= 60 },
    { id: "m5", label: "Buku pertama tamat", desc: "Selesaikan satu buku", done: completed.length >= 1 },
    { id: "m6", label: "Lima buku", desc: "Selesaikan lima buku", done: completed.length >= 5 },
    { id: "m7", label: "500 halaman", desc: "Baca total 500 halaman", done: totalPagesRead >= 500 },
  ];

  return { week, last14: last(14), weekMinutes, weekSessions, streak, totalMinutes, totalPagesRead, reading, completed, completedThisYear, monthMinutes, milestones };
}
